import React, { useEffect, useState } from "react";

interface CBRFeature {
  id: number;
  title: string;
  description: string;
  icon?: string;
}

const FALLBACK_FEATURES: CBRFeature[] = [
  {
    id: 1,
    title: "Objective-oriented",
    description: "Our program is built around specific goals that will transform your spiritual life over 13 weeks of diligent study.",
  },
  {
    id: 2,
    title: "Completely Free",
    description: "The complete package and all materials are completely free.",
  },
  {
    id: 3,
    title: "Self-Paced sessions",
    description: "Run at your own pace so you don't feel overloaded and can adapt to the time you have anytime.",
  },
  {
    id: 4,
    title: "Certificate Awarded",
    description: "We issue a physical certificate upon successful completion of the program.",
  },
];

const WhatIsCBR: React.FC = () => {
  const [features, setFeatures] = useState<CBRFeature[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCBRData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL || ""}/api/program-page-features`);

        if (response.ok) {
          const data = await response.json();
          // Adjust based on the actual API structure, fallback if data empty
          if (data && data.data && data.data.length > 0) {
            setFeatures(data.data);
          } else {
            setFeatures(FALLBACK_FEATURES);
          }
        } else {
          setFeatures(FALLBACK_FEATURES);
        }
      } catch (error) {
        console.error("Error fetching CBR about data:", error);
        setFeatures(FALLBACK_FEATURES);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCBRData();
  }, []);

  const getFeatureIcon = (index: number) => {
    const icons = [
      // Icon 1 - Target/Objective (Blue)
      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-500 mb-6">
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      </div>,
      // Icon 2 - Completely Free (Pink)
      <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center text-pink-500 mb-6">
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>,
      // Icon 3 - Self Paced (Light Blue)
      <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-500 mb-6">
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>,
      // Icon 4 - Certificate (Yellow)
      <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600 mb-6">
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        </svg>
      </div>
    ];
    return icons[index % icons.length];
  };

  return (
    <section className="py-20 bg-gray-50/50">
      <div className="container mx-auto px-4 lg:px-0 text-center mb-16">
        <p className="text-navYellow font-medium tracking-widest text-xl mb-3">About The Program</p>
        <h2 className="text-3xl md:text-4xl font-semibold text-darkBlue">Key Features of the Program</h2>
      </div>

      <div className="container mx-auto px-4 lg:px-0">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-darkBlue"></div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-12">
            {features.map((feature, index) => (
              <div key={feature.id || index} className="bg-gray-50 rounded-3xl p-8 shadow-sm hover:shadow-md transition-shadow border border-gray-100 w-64 flex flex-col flex-wrap">
                {getFeatureIcon(index)}
                <h3 className="text-xl font-semibold text-darkBlue mb-4">{feature.title}</h3>
                <div className="w-full">
                  <p className="text-gray-600 text-sm break-words">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default WhatIsCBR;
